/**
 * @file Initializes Google Analytics 4 (via `react-ga4`), gated on the user's
 * Cookie Information consent for the `cookie_cat_statistic` category.
 *
 * The Cookie Information `uc.js` banner records the user's choices in a
 * `CookieInformationConsent` cookie containing a JSON payload with
 * `consents_approved` and `consents_denied` arrays. When the user accepts or
 * later changes their selection, the script also dispatches a
 * `CookieInformationConsentGiven` event on `window`.
 *
 * `initReactGa` is called at module load on every page entry point, but only
 * forwards to `ReactGA.initialize` once the statistic category is approved —
 * either already on page load (returning visitor with a stored choice) or
 * later via the consent event (first-time visitor accepting the banner).
 * GA initialization is idempotent: the underlying SDK will only ever be
 * initialized once per page lifetime.
 *
 * @see https://nordicsemi.atlassian.net/wiki/spaces/PMO/pages/2835382871
 *      "Cookie Consent installation guide" — implements steps 3 and 4.
 */

import ReactGA from 'react-ga4'

type ReactGAWithInit = { initialize: (measurementId: string) => void }

const STATISTIC_CATEGORY = 'cookie_cat_statistic'
const CONSENT_COOKIE = 'CookieInformationConsent'
const CONSENT_EVENT = 'CookieInformationConsentGiven'

/**
 * Read the `CookieInformationConsent` cookie and check whether the user has
 * approved the statistic category. Returns `false` when the cookie is absent
 * (banner has not been answered yet) or its payload is malformed.
 */
function hasStatisticConsent(): boolean {
	const match = document.cookie.match(
		new RegExp(`(?:^|;\\s*)${CONSENT_COOKIE}=([^;]+)`),
	)
	if (!match) return false
	try {
		const parsed = JSON.parse(decodeURIComponent(match[1])) as {
			consents_approved?: string[]
		}
		return parsed.consents_approved?.includes(STATISTIC_CATEGORY) ?? false
	} catch {
		return false
	}
}

/**
 * Initialize GA4 once the user has approved the `cookie_cat_statistic`
 * consent category. Safe to call regardless of current consent state — the
 * function will defer initialization (via a `CookieInformationConsentGiven`
 * event listener) if consent has not yet been granted.
 *
 * @param measurementId The GA4 measurement ID (e.g. `G-XXXXXXXXXX`).
 *   When empty, the function is a no-op — useful for environments where the
 *   `GA_TRACKING_ID` env var is unset.
 */
export function initReactGa(measurementId: string): void {
	if (measurementId.length === 0) return

	let initialized = false
	const init = () => {
		if (initialized) return
		initialized = true
		;(ReactGA as unknown as ReactGAWithInit).initialize(measurementId)
	}

	if (hasStatisticConsent()) {
		init()
		return
	}

	window.addEventListener(CONSENT_EVENT, () => {
		if (hasStatisticConsent()) init()
	})
}
